
import React from 'react';
import { motion } from 'framer-motion';

// Import the child components it will orchestrate
import { AgentAvatarStage } from './AgentAvatarStage';
import { BuilderAgentChat } from './BuilderAgentChat';
import { SummaryPanelWithCTAs } from './SummaryPanelWithCTAs';
import { SkillsAndStatsStrip, type Skill, type Stat } from './SkillsAndStatsStrip';
import { InvestTeaser } from '../invest/InvestTeaser';

// Import required types
import type { TierId, Range } from '@dopomogai/types';
import type { ChatbotProps, ToolRegistry } from '@dopomogai/chatbot';
import type { InvestmentOfferingConfig, InvestmentStats } from '@dopomogai/types';

/**
 * The props for the AgentStickyRail.
 * This is a pure "data in, actions out" contract, making the component highly reusable.
 */
type Props = {
  // Data for AgentAvatarStage
  avatars: {
    trainee?: string;
    junior?: string;
    middle?: string;
    senior?: string;
    poster?: string;
  };
  renderCosmeticBadge: (id: string) => React.ReactNode;

  // Data for SkillsAndStatsStrip
  skills: Skill[];
  stats: Stat[];
  selectedSkillIds: Set<string>;

  // Data for SummaryPanelWithCTAs
  planTier: TierId;
  tierTooltip?: string;
  setupTotalUSD: number;
  etaDays: Range;
  isQuoteLoading?: boolean;

  // Investment data for InvestTeaser
  investmentOffering?: InvestmentOfferingConfig | null;
  investmentStats?: InvestmentStats | null;
  agentId?: string;
  onInvestClick?: (agentId: string) => void;

  // Chatbot tools configuration
  chatbotTools: {
    toolRegistry: ChatbotProps['toolRegistry'];
    headlessTools: ChatbotProps['headlessTools'];
    headlessContext: ChatbotProps['headlessContext'];
  };
  // Optional: if provided, dock a global chat instance here (e.g., <ChatPortalHost />)
  chatHost?: React.ReactNode;

  // Actions / Callbacks
  onSkillClick: (featureId: string) => void;
  onGenerateQuote: () => void;
  onBookCall?: () => void;
};

export function AgentStickyRail({
  avatars,
  renderCosmeticBadge,
  skills,
  stats,
  selectedSkillIds,
  planTier,
  tierTooltip,
  setupTotalUSD,
  etaDays,
  isQuoteLoading,
  investmentOffering,
  investmentStats,
  agentId,
  onInvestClick,
  chatbotTools,
  chatHost,
  onSkillClick,
  onGenerateQuote,
  onBookCall,
}: Props) {
  // Scroll/highlight then delegate toggle to parent
  const handleSkillClick = (featureId: string) => {
    const element = document.getElementById(`feature-wrapper-${featureId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('highlight-flash');
      setTimeout(() => element.classList.remove('highlight-flash'), 1500);
    }
    onSkillClick(featureId);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 300, damping: 25 } },
  };

  // Ensure BuilderAgentChat always receives a concrete registry
  const resolvedToolRegistry: ToolRegistry = chatbotTools.toolRegistry ?? {};

  return (
    <motion.div className="space-y-4" variants={containerVariants} initial="hidden" animate="visible">
      {/* Agent Avatar Stage */}
      <motion.div variants={itemVariants}>
        <AgentAvatarStage
          videos={avatars}
          badgeRenderer={renderCosmeticBadge}
          ariaLabel="Agent avatar which updates based on selected features"
        />
      </motion.div>

      {/* Skills and Stats */}
      <motion.div variants={itemVariants}>
        <SkillsAndStatsStrip
          skills={skills}
          stats={stats}
          selectedSkillIds={selectedSkillIds}
          onSkillClick={handleSkillClick}
        />
      </motion.div>

      {/* Investment Teaser */}
      {investmentOffering && investmentStats && (
        <motion.div variants={itemVariants}>
          <InvestTeaser
            agentId={agentId || ''}
            offering={investmentOffering}
            stats={investmentStats}
            onInvestClick={onInvestClick}
          />
        </motion.div>
      )}

      {/* Chat: prefer a provided host; do not animate this container to avoid remount loops */}
      <div>
        {chatHost ? (
          chatHost
        ) : (
          <BuilderAgentChat
            toolRegistry={resolvedToolRegistry}
            headlessTools={chatbotTools.headlessTools}
            headlessContext={chatbotTools.headlessContext}
            agentAvatarUrl={
              planTier === 'senior'
                ? avatars.senior
                : planTier === 'middle'
                ? avatars.middle
                : planTier === 'junior'
                ? avatars.junior
                : avatars.trainee || avatars.poster
            }
          />
        )}
      </div>

      {/* Final Summary and CTAs */}
      <motion.div variants={itemVariants}>
        <SummaryPanelWithCTAs
          planTier={planTier}
          tierTooltip={tierTooltip}
          setupTotalUSD={setupTotalUSD}
          etaDays={etaDays}
          onGenerateQuote={onGenerateQuote}
          onBookCall={onBookCall}
          isLoading={isQuoteLoading}
        />
      </motion.div>
    </motion.div>
  );
}

/*
  Add to global CSS to enable the highlight flash:

  @keyframes highlight-flash-anim {
    0% { background-color: transparent; outline-color: transparent; }
    25% { background-color: rgba(16, 185, 129, 0.1); outline: 2px solid rgba(16, 185, 129, 0.4); }
    100% { background-color: transparent; outline-color: transparent; }
  }
  .highlight-flash {
    animation: highlight-flash-anim 1.5s ease-out;
    border-radius: 0.75rem;
    outline-offset: 4px;
  }
*/